import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	TransparentButtonList,
	Space,
	FormattedText,
	FormattedTextReplacement,
	DateString,
	Typography,
	Icon
} from "client/Components";
import { MakeBirthdayWishDialog } from "client/Features/Users/Birthday/MakeBirthdayWishDialog";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	calculateDayDifference,
	getStartOfDay
} from "client/DateAndTime/dateTimeUtils";
import { useUserTimezone } from "client/Hooks";
import { convertToDateTime, parseDtoDateOnlyIgnoringOffset } from "client/DateAndTime/dateUtils";
import { ensureNotNil, setTrackingId } from "client/Misc/utils";
import { useAnalyticsStore } from "client/Features/Analytics/analyticsStore";
import { AppType } from "client/Api/Apps/common";
import { AnalyticsAppEventData } from "client/Api/Analytics/analytics";
import { BirthdayNotificationDataViewModel } from "client/Api/Notifications/notifications";

export type BirthdayActivityProps = {
	data: BirthdayNotificationDataViewModel;
	onActivityChanged: () => void;
};

export const BirthdayActivity = (props: BirthdayActivityProps): ReactElement => {
	const { data, onActivityChanged } = props;

	const {
		firstname,
		lastname,
		userId,
		birthday,
		isWishSent
	} = data;

	const [isWishDialogOpen, setIsWishDialogOpen] = useState<boolean>(false);
	const userTimezone: TimeZone = useUserTimezone();
	const addAnalyticsAppEvent: (data: AnalyticsAppEventData) => void = useAnalyticsStore(x => x.addAppEvent);

	const closeWishDialogHandler = useCallback(
		(didActivityChange?: boolean): void => {
			setIsWishDialogOpen(false);
			if (didActivityChange) {
				onActivityChanged();
			}
		},
		[onActivityChanged]
	);

	const sendWishHandler = useCallback(
		(): void => {
			addAnalyticsAppEvent({
				appType: AppType.Birthday
			});

			setIsWishDialogOpen(true);
		},
		[addAnalyticsAppEvent]
	);

	return (
		<>
			<Icon
				value={{
					kind: "svg",
					name: "party-popper"
				}}
				size="medium"
			/>
			<Space />
			<BirthdayActivityTitle
				firstname={firstname}
				lastname={lastname}
				birthday={birthday}
				timezone={userTimezone}
			/>
			{!isWishSent && (
				<>
					<TransparentButtonList responsiveBehavior="wrap">
						<TransparentButtonList.Item
							bubbleClick={false}
							icon="paper-plane"
							text={localize("_SendBirthdayWishes")}
							onClick={sendWishHandler}
							trackingId="send-birthday-wishes"
						/>
					</TransparentButtonList>
				</>
			)}
			{isWishSent && (
				<>
					<Space />
					{localize("_YouHaveSentYourBirthdayWishes")}
				</>
			)}
			{isWishDialogOpen && (
				<MakeBirthdayWishDialog
					firstName={firstname}
					lastName={lastname}
					userId={userId}
					onClose={() => closeWishDialogHandler()}
					onSend={() => closeWishDialogHandler(true)}
				/>
			)}
		</>
	);
};

BirthdayActivity.displayName = "BirthdayActivity";

type BirthdayActivityTitleProps = {
	firstname: string;
	lastname: string;
	birthday: Date | string;
	timezone: TimeZone;
};

const BirthdayActivityTitle = (props: BirthdayActivityTitleProps): ReactElement => {
	const {
		firstname,
		lastname,
		birthday,
		timezone
	} = props;

	const fullname: string = `${firstname} ${lastname}`;

	const today: Date = getStartOfDay(
		new Date(),
		timezone
	);

	const birthdayDate: Date = useMemo(
		(): Date => {
			if (typeof birthday === "string") {
				return convertToDateTime(
					ensureNotNil(
						parseDtoDateOnlyIgnoringOffset(birthday)
					),
					timezone
				);
			} else {
				return getStartOfDay(birthday, timezone);
			}
		},
		[birthday, timezone]
	);

	const diffInDays: number = calculateDayDifference(
		birthdayDate,
		today,
		timezone
	);

	let key: LocalizationKey | undefined;
	const replacements: readonly FormattedTextReplacement[] = [
		{
			what: "{name}",
			filler: (
				<Typography
					trackingId="user-name"
					weight="bold"
				>
					{fullname}
				</Typography>
			)
		},
		{
			what: "{date}",
			filler: (
				<DateString
					value={birthdayDate}
					format="RelativeDay"
					timezone={timezone}
				/>
			)
		}
	];

	if (diffInDays === 1) {
		key = "_XHasABirthdayTomorrow";
	} else if (diffInDays === 0) {
		key = "_XHasABirthdayToday";
	} else if (diffInDays === -1) {
		key = "_XHadABirthdayYesterday";
	} else if (diffInDays <= -2) {
		key = "_XHadABirthdayOn";
	} else {
		key = "_XHasABirthdayOn";
	}

	return (
		<span {...setTrackingId("title")}>
			<FormattedText
				text={localize(key)}
				thingsToReplace={replacements}
			/>
		</span>
	);
};

BirthdayActivityTitle.displayName = "BirthdayActivityTitle";
